import { createQuery } from "@/api/utils/createQuery";
import {
    FileSnapshot,
    StatementType,
} from "@/entities/DataAssociation/File.model";
import { QueryStringBuilder } from "@/utils";
import { File } from "@/entities/FileService/File.model";
import { ApiClient } from "@/api/types/client.types";

const STATEMENT_TYPES = [
    StatementType.INCOME_STATEMENT,
    StatementType.BALANCE_SHEET,
] as const;

type FileWithLabels = FileSnapshot & {
    labeledFiles: { id: string; name: string }[];
};

type StatementTypeKey = Exclude<StatementType, StatementType.MIXED>;

export type ConnectorFilesResponse = {
    sourceConnectorId: string;
    sourceConnectorType: string;
    mostRecent: Record<StatementTypeKey, FileWithLabels | null>;
    historical: Record<StatementTypeKey, FileWithLabels | null>;
    current: FileWithLabels | null;
};

type DescendantTree = {
    id: string;
    name: string;
    descendants?: DescendantTree[];
};

const flattenDescendants = (
    tree: DescendantTree,
): { id: string; name: string }[] => {
    const result: { id: string; name: string }[] = [];
    if (tree.descendants) {
        for (const d of tree.descendants) {
            result.push({ id: d.id, name: d.name });
            result.push(...flattenDescendants(d));
        }
    }
    return result;
};

export async function getHistoricalFiles(
    api: ApiClient,
    companyId: string,
): Promise<File[]> {
    const query = new QueryStringBuilder()
        .equals("company_id", companyId)
        .equals("source", "codat")
        .equals("document_type", "raw")
        .add("max_page_size", "999")
        .build();
    const response = await api
        .get(`/file/files?${query}`)
        .then((response) =>
            response.items.filter((f) => f.meta?.isHistoricalPull),
        );
    return response;
}

export async function getCleanedConnectorFiles(
    api: ApiClient,
    companyId: string,
): Promise<FileSnapshot[]> {
    const query = new QueryStringBuilder()
        .equals("company_id", companyId)
        .equals("source_type", "connector")
        .equals("document_type", "cleaned")
        .add("max_page_size", "999")
        .build();
    const response = await api.get(`/data-association/files?${query}`);
    return response.items;
}

export async function getAssociatedUploads(
    api: ApiClient,
    companyId: string,
): Promise<FileSnapshot[]> {
    const query = new QueryStringBuilder()
        .equals("company_id", companyId)
        .equals("source_type", "uploaded_file")
        .add("max_page_size", "999")
        .build();
    const response = await api.get(`/data-association/files?${query}`);
    return response.items;
}

export function getMostRecentFile(files: { id: string; createdAt: string }[]) {
    return files.sort(
        (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0];
}

export function getMostRecentByStatementType(
    files: FileSnapshot[],
): Record<StatementTypeKey, FileSnapshot | null> {
    const result: Record<StatementTypeKey, FileSnapshot | null> = {
        income_statement: null,
        balance_sheet: null,
    };

    for (const statementType of STATEMENT_TYPES) {
        console.log("statementType", statementType, files);
        const filtered = files
            .filter((file) => file.documentKind === statementType)
            .sort((a, b) => {
                console.log(
                    "a",
                    new Date(a.createdAt).getTime(),
                    new Date(b.createdAt).getTime(),
                );
                return (
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
                );
            });
        result[statementType] = filtered[0] ?? null;
    }

    return result;
}

export async function hydrateWithLabeledFiles<T extends { id: string }>(
    file: T | null,
    api: ApiClient,
): Promise<(T & { labeledFiles: { id: string; name: string }[] }) | null> {
    if (!file) return null;
    const labeledFiles = await api
        .get(`/file/files/${file.id}/descendants`)
        .then(flattenDescendants)
        .then((nodes) =>
            nodes
                .filter((node) => node.name.startsWith("labeled-"))
                .map((node) => ({
                    id: node.id,
                    name: node.name,
                })),
        );
    return {
        ...file,
        labeledFiles,
    };
}

export const getConnectorFiles = createQuery<ConnectorFilesResponse[]>(
    async ({ api, router }) => {
        if (!router.params.companyId) {
            return [];
        }

        const cleanedFiles = await getCleanedConnectorFiles(
            api,
            router.params.companyId,
        );

        const connectors = [
            ...new Map(
                cleanedFiles
                    .filter(
                        (f) =>
                            (f.meta.rawConnector?.kind &&
                                f.meta.rawConnector?.id) ||
                            f.sourceConnectorId,
                    )
                    .map((f) => {
                        const connectorId =
                            f.meta.rawConnector?.id || f.sourceConnectorId!;
                        const connectorType =
                            f.meta.rawConnector?.kind || "unknown";
                        return [
                            `${connectorType}:${connectorId}`,
                            {
                                sourceConnectorType: connectorType,
                                sourceConnectorId: connectorId,
                            },
                        ];
                    }),
            ).values(),
        ];

        const currentId = router.params.fileId;

        const results = await Promise.all(
            connectors.map(async (connector) => {
                const connectorCleanedFiles = cleanedFiles.filter(
                    (f) =>
                        (f.meta.rawConnector?.kind ===
                            connector.sourceConnectorType &&
                            f.meta.rawConnector?.id ===
                                connector.sourceConnectorId) ||
                        f.sourceConnectorId === connector.sourceConnectorId,
                );

                const rawHistoricalFiles = await getHistoricalFiles(
                    api,
                    router.params.companyId || "",
                );
                console.log("rawHistoricalFiles", rawHistoricalFiles);
                const historicalFiles = getMostRecentByStatementType(
                    (await rawHistoricalFiles)
                        .map((f) =>
                            connectorCleanedFiles.find(
                                (cf) => cf.producedFromFile === f.id,
                            ),
                        )
                        .filter((f): f is FileSnapshot => f !== undefined) ??
                        [],
                );

                const mostRecent = getMostRecentByStatementType(
                    connectorCleanedFiles.filter(
                        (f) =>
                            !Object.values(historicalFiles).some(
                                (h) => h?.id === f.id,
                            ),
                    ),
                );

                const hasCurrent =
                    !Object.values(mostRecent).some(
                        (f) => f?.id === currentId,
                    ) &&
                    !Object.values(historicalFiles).some(
                        (f) => f?.id === currentId,
                    );
                const current = hasCurrent
                    ? connectorCleanedFiles.find((f) => f.id === currentId) ??
                      null
                    : null;

                const [
                    mostRecentIS,
                    mostRecentBS,
                    historicalIS,
                    historicalBS,
                    currentHydrated,
                ] = await Promise.all([
                    hydrateWithLabeledFiles(mostRecent.income_statement, api),
                    hydrateWithLabeledFiles(mostRecent.balance_sheet, api),
                    hydrateWithLabeledFiles(
                        historicalFiles.income_statement,
                        api,
                    ),
                    hydrateWithLabeledFiles(historicalFiles.balance_sheet, api),
                    hydrateWithLabeledFiles(current, api),
                ]);

                return {
                    sourceConnectorId: connector.sourceConnectorId,
                    sourceConnectorType: connector.sourceConnectorType,
                    mostRecent: {
                        income_statement: mostRecentIS,
                        balance_sheet: mostRecentBS,
                    },
                    historical: {
                        income_statement: historicalIS,
                        balance_sheet: historicalBS,
                    },
                    current: currentHydrated,
                };
            }),
        );

        console.log("results", results);

        return results;
    },
);

export type CleanedChildWithLabels = {
    id: string;
    name: string;
    labeledFiles: { id: string; name: string }[];
};

export type UploadWithChildren = File & {
    cleanedChildren: CleanedChildWithLabels[];
};

export type UploadsResponse = {
    labeled: UploadWithChildren[];
    unlabeled: UploadWithChildren[];
};

const extractCleanedWithLabeled = (
    tree: DescendantTree,
): CleanedChildWithLabels[] => {
    const result: CleanedChildWithLabels[] = [];

    const traverse = (node: DescendantTree) => {
        if (node.name.startsWith("cl-")) {
            const labeledFiles = (node.descendants ?? [])
                .filter((d) => d.name.startsWith("labeled-"))
                .map((d) => ({ id: d.id, name: d.name }));
            result.push({
                id: node.id,
                name: node.name,
                labeledFiles,
            });
        }
        node.descendants?.forEach(traverse);
    };

    tree.descendants?.forEach(traverse);
    return result;
};

export const getUploads = createQuery<UploadsResponse>(
    async ({ api, router }) => {
        const query = new QueryStringBuilder()
            .equals("company_id", router.params.companyId || "")
            .equals("source", "user_uploaded")
            .equals("document_type", "raw")
            .add("max_page_size", "999")
            .build();
        const response = await api.get(`/file/files?${query}`);
        const rawFiles: File[] = response.items;
        const associatedUploads = await getAssociatedUploads(
            api,
            router.params.companyId || "",
        );

        const uploadsWithChildren = await Promise.all(
            rawFiles.map(async (file) => {
                const descendants: DescendantTree = await api.get(
                    `/file/files/${file.id}/descendants`,
                );
                const cleanedChildren = extractCleanedWithLabeled(descendants);
                return {
                    ...file,
                    cleanedChildren,
                };
            }),
        );

        const labeled = uploadsWithChildren
            .filter((u) =>
                u.cleanedChildren.some((c) => c.labeledFiles.length > 0),
            )
            .map((u) => ({
                ...u,
                associatedUpload: associatedUploads.find((a) => a.id === u.id),
            }));
        const unlabeled = uploadsWithChildren
            .filter(
                (u) =>
                    !u.cleanedChildren.some((c) => c.labeledFiles.length > 0),
            )
            .map((u) => ({
                ...u,
                associatedUpload: associatedUploads.find((a) => a.id === u.id),
            }));

        return { labeled, unlabeled };
    },
);
